import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { AnimatedPage, AnimatedSection, metricMotion } from '../components/animations/MotionContainers';
import { DailyEvolutionCard } from '../components/dashboard/DailyEvolutionCard';
import { SummaryMetricCard } from '../components/dashboard/SummaryMetricCard';
import { WarehouseDistributionCard } from '../components/dashboard/WarehouseDistributionCard';
import { VariedadNavAutocomplete } from '../components/entradas/VariedadNavAutocomplete';
import { useFlujosCatalogs } from '../hooks/useFlujosCatalogs';
import { useFlujosEntradas } from '../hooks/useFlujosEntradas';
import { useFlujosVariedadesNav } from '../hooks/useFlujosVariedadesNav';
import {
  formatDisplayDate,
  getCampaignOptionsFromEntries,
  getCurrentCampaignRange,
  getDailyEvolution,
} from '../utils/dashboardData';
import {
  calculateFlowTotals,
  formatKilos,
  formatPercent,
  groupFieldByWarehouse,
} from '../utils/flujoCalculations';
import { isDateInRange } from '../utils/dateFormatters';

const warehouseColors = {
  1: '#ff7900',
  2: '#1570ef',
  3: '#079455',
};

const filterResultsMotion = {
  hidden: {
    opacity: 0,
    y: 12,
    filter: 'blur(2px)',
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.22,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.045,
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    filter: 'blur(1px)',
    transition: {
      duration: 0.16,
      ease: 'easeIn',
    },
  },
};

export const HistoricoVariedad = () => {
  const reduceMotion = useReducedMotion();
  const {
    almacenes,
    variedades: variedadesFlujos,
    cajasPalet,
    loading: catalogsLoading,
    error: catalogsError,
  } = useFlujosCatalogs();
  const {
    rows: variedades,
    loading: variedadesLoading,
    error: variedadesError,
  } = useFlujosVariedadesNav();
  const { rows: entradas, loading: entradasLoading, error: entradasError } = useFlujosEntradas({
    limit: 500,
    sortBy: 'fecha',
    sortDirection: 'desc',
  }, { almacenes, variedades: variedadesFlujos, cajasPalet });
  const currentCampaignStartYear = getCurrentCampaignRange().startYear;
  const campaignOptions = useMemo(
    () => getCampaignOptionsFromEntries(entradas),
    [entradas]
  );
  const [selectedCampaignStartYear, setSelectedCampaignStartYear] = useState(currentCampaignStartYear);
  const selectedCampaign = useMemo(
    () => campaignOptions.find((campaign) => campaign.startYear === selectedCampaignStartYear) ||
      getCurrentCampaignRange(),
    [campaignOptions, selectedCampaignStartYear]
  );
  const campaignRows = useMemo(
    () => entradas.filter((row) => (
      isDateInRange(row.fecha, selectedCampaign.startValue, selectedCampaign.endValue)
    )),
    [entradas, selectedCampaign]
  );
  const [selectedVarietyId, setSelectedVarietyId] = useState('');

  useEffect(() => {
    if (
      variedades.length > 0 &&
      !variedades.some((variedad) => variedad.id === selectedVarietyId)
    ) {
      setSelectedVarietyId(variedades[0].id);
    }
  }, [selectedVarietyId, variedades]);

  const selectedVariety = variedades.find((variedad) => variedad.id === selectedVarietyId);
  const varietyRows = useMemo(
    () => campaignRows.filter((row) => String(row.idVariedad) === String(selectedVarietyId)),
    [campaignRows, selectedVarietyId]
  );
  const totals = calculateFlowTotals(varietyRows);
  const industryPercent = totals.kilosClasificados > 0
    ? (totals.industria * 100) / totals.kilosClasificados
    : 0;
  const rottenPercent = totals.kilosClasificados > 0
    ? (totals.podrido * 100) / totals.kilosClasificados
    : 0;
  const warehouseKilos = groupFieldByWarehouse(varietyRows, almacenes, 'kilosVolcados');
  const dailyEvolution = getDailyEvolution(varietyRows, almacenes, warehouseColors);
  const resultsAnimationKey = `${selectedCampaignStartYear}-${selectedVarietyId}`;

  return (
    <AnimatedPage>
        <Stack spacing={2.5}>
          <AnimatedSection>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' } }}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                  }}
                >
                  <CategoryRoundedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h1">Historico por variedad</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rendimiento, calidad y evolucion diaria por variedad
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
              >
                <Box
                  sx={{
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    minWidth: { xs: '100%', sm: 270 },
                  }}
                >
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <CalendarMonthOutlinedIcon color="primary" fontSize="medium" />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ color: 'primary.main', fontSize: 12, fontWeight: 900, lineHeight: '15px' }}>
                        Campaña
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: '14px' }}>
                        {formatDisplayDate(selectedCampaign.startValue)} - {formatDisplayDate(selectedCampaign.endValue)}
                      </Typography>
                    </Box>
                    <TextField
                      select
                      size="small"
                      value={selectedCampaignStartYear}
                      onChange={(event) => setSelectedCampaignStartYear(Number(event.target.value))}
                      sx={{ minWidth: 112 }}
                    >
                      {campaignOptions.map((campaign) => (
                        <MenuItem key={campaign.startYear} value={campaign.startYear}>
                          {campaign.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    minWidth: { xs: '100%', sm: 380 },
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
                  >
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1, minWidth: 0 }}>
                      <CategoryRoundedIcon color="primary" fontSize="medium" />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: 'primary.main', fontSize: 12, fontWeight: 900, lineHeight: '15px' }}>
                          Variedad
                        </Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: '14px' }}>
                          Analisis seleccionado
                        </Typography>
                      </Box>
                    </Stack>
                    <VariedadNavAutocomplete
                      options={variedades}
                      size="small"
                      value={selectedVarietyId}
                      onChange={setSelectedVarietyId}
                      label=""
                      loading={variedadesLoading}
                      sx={{ width: { xs: '100%', sm: 210 }, flexShrink: 0 }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </AnimatedSection>

          {(entradasLoading || catalogsLoading || variedadesLoading || entradasError || catalogsError || variedadesError) && (
            <Alert
              severity={entradasError || catalogsError || variedadesError ? 'error' : 'info'}
              variant="outlined"
            >
              {entradasError || catalogsError || variedadesError || 'Cargando datos...'}
            </Alert>
          )}

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={resultsAnimationKey}
              initial={reduceMotion ? false : 'hidden'}
              animate={reduceMotion ? undefined : 'visible'}
              exit={reduceMotion ? undefined : 'exit'}
              variants={reduceMotion ? undefined : filterResultsMotion}
            >
              <Stack spacing={2.5}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <AnimatedSection variant={metricMotion}>
                      <SummaryMetricCard
                        icon={<ScaleOutlinedIcon />}
                        title="Kilos volcados"
                        value={formatKilos(totals.kilosVolcados)}
                        suffix="kg"
                      />
                    </AnimatedSection>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <AnimatedSection variant={metricMotion}>
                      <SummaryMetricCard
                        icon={<Inventory2OutlinedIcon />}
                        title="Kilos clasificados"
                        value={formatKilos(totals.kilosClasificados)}
                        suffix="kg"
                      />
                    </AnimatedSection>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <AnimatedSection variant={metricMotion}>
                      <SummaryMetricCard
                        icon={<PercentOutlinedIcon />}
                        title="Rendimiento medio"
                        value={formatPercent(totals.rendimiento)}
                        suffix="%"
                      />
                    </AnimatedSection>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <AnimatedSection variant={metricMotion}>
                      <SummaryMetricCard
                        icon={<WarningAmberOutlinedIcon />}
                        title="% industria"
                        value={formatPercent(industryPercent)}
                        suffix="%"
                      />
                    </AnimatedSection>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}>
                    <AnimatedSection variant={metricMotion}>
                      <SummaryMetricCard
                        icon={<WarningAmberOutlinedIcon />}
                        title="% podrido"
                        value={formatPercent(rottenPercent)}
                        suffix="%"
                      />
                    </AnimatedSection>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <AnimatedSection>
                      <WarehouseDistributionCard
                        title={`Kilos volcados por almacen${selectedVariety ? ` - ${selectedVariety.nombre}` : ''}`}
                        rows={warehouseKilos}
                        colors={warehouseColors}
                      />
                    </AnimatedSection>
                  </Grid>
                  <Grid size={{ xs: 12, lg: 8 }}>
                    <AnimatedSection>
                      <DailyEvolutionCard rows={dailyEvolution} showSegments />
                    </AnimatedSection>
                  </Grid>
                </Grid>
              </Stack>
            </motion.div>
          </AnimatePresence>
        </Stack>
    </AnimatedPage>
  );
};
